import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { Card, dayName, shortDate, useToast } from '../components/ui';
import type { Member, SlotChoice, SlotType } from '../types';
import type { DashboardModuleProps } from './registry';

const T = {
  en: {
    title: "Who's eating this week",
    subtitle: 'Set this before planning. Anything switched off is simply not planned.',
    edit: 'Change',
    close: 'Done',
    save: 'Save and re-plan',
    saving: 'Saving…',
    saved: 'Week set up',
    quick: 'Quick',
    quickHint: 'Keep it under 30 minutes',
    breakfast: 'Breakfast',
    morning_snack: 'Snack box',
    lunch: 'Lunch',
    gouter: 'Goûter',
    dinner: 'Dinner',
    off: 'Not at home',
    everyone: 'Everyone',
    adults: 'Adults',
    kids: 'Kids',
    separate: 'Separate',
    choose: 'Choose people',
    on: 'Yes',
    dessert: 'Dessert',
    evening_drink: 'Evening drink',
    aperitif_nibble: 'Something with it',
    drinkOff: 'None',
    drinkOn: 'Yes',
    drinkPlus: 'With a nibble',
    alcDefault: 'As usual',
    alcYes: 'With alcohol',
    alcNo: 'Without',
    guests: 'Guests',
    guestsHint: 'People eating who are not in the household',
    summary: (m: number, l: number, g: number, d: number) =>
      `${d} dinners · ${l} lunches · ${m} snack boxes · ${g} goûters`,
    allGone: 'This week is over. Use the arrows above to plan the week ahead.',
    someGone: (n: number) =>
      n === 1 ? 'One day this week has already gone.' : `${n} days this week have already gone.`,
    legend:
      'Adults = the children are elsewhere. Kids = the adults are out. Separate = everyone eats, but different meals.',
  },
  fr: {
    title: 'Qui mange cette semaine',
    subtitle: 'À définir avant de planifier. Ce qui est désactivé ne sera pas planifié.',
    edit: 'Modifier',
    close: 'Terminé',
    save: 'Enregistrer et replanifier',
    saving: 'Enregistrement…',
    saved: 'Semaine configurée',
    quick: 'Rapide',
    quickHint: 'Moins de 30 minutes',
    breakfast: 'Petit-déjeuner',
    morning_snack: 'Collation',
    lunch: 'Déjeuner',
    gouter: 'Goûter',
    dinner: 'Dîner',
    off: 'Absents',
    everyone: 'Tous',
    adults: 'Adultes',
    kids: 'Enfants',
    separate: 'Séparé',
    choose: 'Choisir',
    on: 'Oui',
    dessert: 'Dessert',
    evening_drink: 'Apéritif',
    aperitif_nibble: 'À grignoter',
    drinkOff: 'Aucun',
    drinkOn: 'Oui',
    drinkPlus: 'Avec à grignoter',
    alcDefault: 'Comme d’habitude',
    alcYes: 'Alcoolisé',
    alcNo: 'Sans alcool',
    guests: 'Invités',
    guestsHint: 'Personnes à table qui ne font pas partie du foyer',
    summary: (m: number, l: number, g: number, d: number) =>
      `${d} dîners · ${l} déjeuners · ${m} collations · ${g} goûters`,
    allGone: 'Cette semaine est passée. Utilisez les flèches ci-dessus pour planifier la suivante.',
    someGone: (n: number) =>
      n === 1 ? 'Un jour de cette semaine est déjà passé.' : `${n} jours de cette semaine sont déjà passés.`,
    legend:
      "Adultes = les enfants sont ailleurs. Enfants = les adultes sortent. Séparé = tout le monde mange, mais des plats différents.",
  },
} as const;

const MEAL_SLOTS: SlotType[] = ['breakfast', 'lunch', 'dinner', 'dessert'];
/** Adults only, and on or off — there is no 'the children have one' (§10). */
const DRINK_SLOTS: SlotType[] = ['evening_drink', 'aperitif_nibble'];
/** Sittings a guest can plausibly be at, so the count is worth asking for. */
const GUEST_SLOTS: SlotType[] = ['lunch', 'dinner', 'dessert', 'evening_drink'];
const SNACK_SLOTS: SlotType[] = ['morning_snack', 'gouter'];

/**
 * The order a day actually happens in.
 *
 * The two lists above say which controls a sitting gets — a snack is on or
 * off, a meal can be split — but they are not the order anybody reads a day
 * in, so the rows are laid out by this instead.
 */
const DAY_ORDER: SlotType[] = [
  'breakfast',
  'morning_snack',
  'lunch',
  'gouter',
  'dinner',
  'dessert',
  'evening_drink',
];

interface DayPlan {
  date: string;
  /** Already happened — nothing here is a decision any more. */
  isPast: boolean;
  choices: Record<SlotType, SlotChoice>;
  quick: Partial<Record<SlotType, boolean>>;
  /**
   * Explicit diner list for a sitting, when the family has picked individuals
   * rather than a whole group — "only two of the three at Wednesday lunch", or
   * the au pair eating with the children.
   */
  people: Partial<Record<SlotType, string[]>>;
  /** Extra mouths at a sitting who are not household members (§8). */
  guests: Partial<Record<SlotType, number>>;
  /**
   * Whether tonight's drink has alcohol in it. Unset means the household's
   * usual answer, which is the setting on the Settings page (§10).
   */
  drinkAlcoholic: boolean | null;
}

/**
 * Setting up the week (§8) — which sittings need planning, and who is at each.
 *
 * The engine has always supported this; this is the screen that lets a family
 * actually say "Tuesday the adults are out, Wednesday the children are at
 * friends for dinner".
 */
export function WeekSetupModule({ week, language, refresh }: DashboardModuleProps) {
  const t = T[language];
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [plan, setPlan] = useState<DayPlan[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api.household().then((result) => setMembers(result.members));
  }, []);

  // Derive the current shape of the week from what is stored.
  useEffect(() => {
    if (!week) return;
    setPlan(
      week.days.map((day) => {
        const isPast = day.isPast;
        const choices = {
          breakfast: 'off',
          morning_snack: 'off',
          lunch: 'off',
          gouter: 'off',
          dinner: 'off',
          dessert: 'off',
          evening_drink: 'off',
          aperitif_nibble: 'off',
        } as Record<SlotType, SlotChoice>;
        const quick: Partial<Record<SlotType, boolean>> = {};
        const people: Partial<Record<SlotType, string[]>> = {};
        const guests: Partial<Record<SlotType, number>> = {};
        let drinkAlcoholic: boolean | null = null;

        for (const slot of day.slots) {
          const type = slot.slotType as SlotType;
          quick[type] = slot.prepRequirement === 'quick';
          if (slot.guestCount > 0) guests[type] = slot.guestCount;
          if (type === 'evening_drink') drinkAlcoholic = slot.drinkAlcoholic;

          people[type] = [...new Set(slot.dishes.flatMap((d) => d.diners.map((p) => p.id)))];

          if (type === 'morning_snack' || type === 'gouter') {
            choices[type] = 'kids';
            continue;
          }
          if (type === 'evening_drink' || type === 'aperitif_nibble') {
            choices[type] = 'adults';
            continue;
          }
          const audiences = new Set(slot.dishes.map((d) => d.audience));
          if (slot.split || audiences.size > 1) choices[type] = 'separate';
          else if (audiences.has('everyone')) choices[type] = 'everyone';
          else if (audiences.has('adults')) choices[type] = 'adults';
          else if (audiences.has('kids')) choices[type] = 'kids';
        }
        return { date: day.date, isPast, choices, quick, people, guests, drinkAlcoholic };
      }),
    );
  }, [week]);

  const counts = useMemo(() => {
    const ahead = plan.filter((d) => !d.isPast);
    const on = (type: SlotType) => ahead.filter((d) => d.choices[type] !== 'off').length;
    return { m: on('morning_snack'), l: on('lunch'), g: on('gouter'), d: on('dinner') };
  }, [plan]);

  if (!week) return null;

  const gone = plan.filter((day) => day.isPast).length;

  const set = (date: string, type: SlotType, choice: SlotChoice) =>
    setPlan((current) =>
      current.map((day) =>
        day.date === date ? { ...day, choices: { ...day.choices, [type]: choice } } : day,
      ),
    );

  /** Choosing individuals switches the sitting to an explicit list of people. */
  const togglePerson = (date: string, type: SlotType, memberId: string) =>
    setPlan((current) =>
      current.map((day) => {
        if (day.date !== date) return day;
        const chosen = day.people[type] ?? defaultPeopleFor(day.choices[type]).map((m) => m.id);
        const next = chosen.includes(memberId)
          ? chosen.filter((id) => id !== memberId)
          : [...chosen, memberId];
        return { ...day, people: { ...day.people, [type]: next } };
      }),
    );

  /**
   * Guests at a sitting.
   *
   * Setting them on dinner carries the same number onto that evening's dessert
   * and drink, because someone who comes to dinner is at the table for those
   * too — and typing the number three times is exactly the kind of data entry
   * this app is supposed to remove. The other rows stay editable, so a guest
   * who leaves before pudding can be taken off it.
   */
  const setGuests = (date: string, type: SlotType, count: number) =>
    setPlan((current) =>
      current.map((day) => {
        if (day.date !== date) return day;
        const next = { ...day.guests, [type]: Math.max(0, count) };
        if (type === 'evening_drink') next.aperitif_nibble = Math.max(0, count);
        if (type === 'dinner') {
          const before = day.guests.dinner ?? 0;
          for (const follows of [
            'dessert',
            'evening_drink',
            'aperitif_nibble',
          ] as SlotType[]) {
            if ((day.guests[follows] ?? 0) === before) next[follows] = Math.max(0, count);
          }
        }
        return { ...day, guests: next };
      }),
    );

  const setDrinkAlcoholic = (date: string, value: boolean | null) =>
    setPlan((current) =>
      current.map((day) => (day.date === date ? { ...day, drinkAlcoholic: value } : day)),
    );

  const toggleQuick = (date: string, type: SlotType) =>
    setPlan((current) =>
      current.map((day) =>
        day.date === date ? { ...day, quick: { ...day.quick, [type]: !day.quick[type] } } : day,
      ),
    );

  const save = async () => {
    // Only the days still ahead. Saving a shape for last Tuesday would rewrite
    // a record of what actually happened.
    const slots = plan.filter((day) => !day.isPast).flatMap((day) =>
      ([...SNACK_SLOTS, ...MEAL_SLOTS, ...DRINK_SLOTS] as SlotType[]).flatMap((type) => {
        const choice = day.choices[type];
        if (choice === 'off') return [];

        // An explicit list always wins over the group shortcut.
        const explicit = day.people[type];
        const who =
          explicit && explicit.length > 0
            ? members.filter((m) => explicit.includes(m.id))
            : defaultPeopleFor(choice, type);
        if (who.length === 0) return [];

        return [
          {
            date: day.date,
            slotType: type,
            prepRequirement: day.quick[type] ? 'quick' : 'any',
            split: choice === 'separate',
            guestCount: day.guests[type] ?? 0,
            drinkAlcoholic: type === 'evening_drink' ? day.drinkAlcoholic : null,
            members: who.map((m) => ({ memberId: m.id, dietMode: m.default_diet_mode })),
          },
        ];
      }),
    );

    setSaving(true);
    try {
      await api.setSlots(week.weekStart, slots, language);
      await api.generate(week.weekStart, language);
      toast(t.saved);
      setOpen(false);
      refresh();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save the week');
    } finally {
      setSaving(false);
    }
  };

  /** Who a group shortcut means. An au pair eats as an adult by default. */
  function defaultPeopleFor(choice: SlotChoice, type?: SlotType): Member[] {
    if (type === 'morning_snack' || type === 'gouter') {
      return members.filter((m) => m.member_type === 'child');
    }
    // The evening drink is the adults', whatever the shortcut says (§10).
    if (type === 'evening_drink') return members.filter((m) => m.member_type !== 'child');
    if (choice === 'adults') return members.filter((m) => m.member_type !== 'child');
    if (choice === 'kids') return members.filter((m) => m.member_type === 'child');
    return members;
  }

  /**
   * The drink and its nibble are one decision with three answers, so they are
   * set together — nobody wants a nibble and no drink.
   */
  const setDrink = (date: string, mode: 'off' | 'drink' | 'both') =>
    setPlan((current) =>
      current.map((day) =>
        day.date === date
          ? {
              ...day,
              choices: {
                ...day.choices,
                evening_drink: mode === 'off' ? 'off' : 'adults',
                aperitif_nibble: mode === 'both' ? 'adults' : 'off',
              },
            }
          : day,
      ),
    );

  /** How many extra mouths, as a stepper — the number is always small. */
  const guestStepper = (day: DayPlan, type: SlotType) => {
    const count = day.guests[type] ?? 0;
    return (
      <span className="guest-stepper" title={t.guestsHint}>
        <button
          type="button"
          className="btn small"
          disabled={count === 0}
          aria-label={`${t.guests} −`}
          onClick={() => setGuests(day.date, type, count - 1)}
        >
          −
        </button>
        <span className={count > 0 ? 'guest-count on' : 'guest-count'}>
          {count > 0 ? `+${count}` : t.guests}
        </span>
        <button
          type="button"
          className="btn small"
          aria-label={`${t.guests} +`}
          onClick={() => setGuests(day.date, type, count + 1)}
        >
          +
        </button>
      </span>
    );
  };

  const choiceButton = (day: DayPlan, type: SlotType, value: SlotChoice, label: string) => (
    <button
      key={value}
      type="button"
      className={`btn small${day.choices[type] === value ? ' on' : ''}`}
      aria-pressed={day.choices[type] === value}
      onClick={() => {
        set(day.date, type, value);
        // Picking a group shortcut clears any hand-picked list.
        setPlan((current) =>
          current.map((d) =>
            d.date === day.date ? { ...d, people: { ...d.people, [type]: undefined } } : d,
          ),
        );
      }}
    >
      {label}
    </button>
  );

  /** Per-person checkboxes, so any combination of people can be set. */
  const peoplePicker = (day: DayPlan, type: SlotType) => {
    const chosen = day.people[type] ?? defaultPeopleFor(day.choices[type], type).map((m) => m.id);
    return (
      <div className="people-picker">
        {members.map((member) => (
          <button
            key={member.id}
            type="button"
            className={`btn small${chosen.includes(member.id) ? ' on' : ''}`}
            aria-pressed={chosen.includes(member.id)}
            onClick={() => togglePerson(day.date, type, member.id)}
          >
            {member.name}
          </button>
        ))}
      </div>
    );
  };

  return (
    <Card
      title={
        <div>
          <h2>{t.title}</h2>
          <p className="dim">{counts.d + counts.l > 0 ? t.summary(counts.m, counts.l, counts.g, counts.d) : t.subtitle}</p>
        </div>
      }
      actions={
        <button className="btn" onClick={() => setOpen((v) => !v)}>
          {open ? t.close : t.edit}
        </button>
      }
    >
      {!open ? (
        <p className="dim">{t.subtitle}</p>
      ) : (
        <div className="stack" style={{ gap: 4 }}>
          <p className="dim" style={{ marginBottom: 8 }}>
            {t.legend}
          </p>

          {/* Say what is missing rather than quietly showing four days. */}
          {gone === plan.length ? (
            <p className="banner">{t.allGone}</p>
          ) : (
            gone > 0 && <p className="dim">{t.someGone(gone)}</p>
          )}

          {plan
            .filter((day) => !day.isPast)
            .map((day) => (
            <div key={day.date} className="setup-day">
              <div className="day-head" style={{ padding: '10px 0 6px' }}>
                <span className="date">{dayName(day.date, language)}</span>
                <span className="rel">{shortDate(day.date, language)}</span>
              </div>

              {DAY_ORDER.map((type) =>
                type === 'evening_drink' ? (
                  <div className="setup-row" key={type}>
                    <span className="setup-label">{t.evening_drink}</span>
                    <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className={`btn small${day.choices.evening_drink === 'off' ? ' on' : ''}`}
                        aria-pressed={day.choices.evening_drink === 'off'}
                        onClick={() => setDrink(day.date, 'off')}
                      >
                        {t.drinkOff}
                      </button>
                      <button
                        type="button"
                        className={`btn small${
                          day.choices.evening_drink === 'adults' &&
                          day.choices.aperitif_nibble === 'off'
                            ? ' on'
                            : ''
                        }`}
                        onClick={() => setDrink(day.date, 'drink')}
                      >
                        {t.drinkOn}
                      </button>
                      <button
                        type="button"
                        className={`btn small${
                          day.choices.aperitif_nibble === 'adults' ? ' on' : ''
                        }`}
                        onClick={() => setDrink(day.date, 'both')}
                      >
                        {t.drinkPlus}
                      </button>
                      {day.choices.evening_drink !== 'off' && (
                        <>
                          <span className="setup-sep" aria-hidden="true">
                            ·
                          </span>
                          {(
                            [
                              [null, t.alcDefault],
                              [true, t.alcYes],
                              [false, t.alcNo],
                            ] as Array<[boolean | null, string]>
                          ).map(([value, label]) => (
                            <button
                              key={String(value)}
                              type="button"
                              className={`btn small${day.drinkAlcoholic === value ? ' on' : ''}`}
                              aria-pressed={day.drinkAlcoholic === value}
                              onClick={() => setDrinkAlcoholic(day.date, value)}
                            >
                              {label}
                            </button>
                          ))}
                        </>
                      )}
                      {day.choices.evening_drink !== 'off' &&
                        guestStepper(day, 'evening_drink')}
                    </div>
                  </div>
                ) : type === 'aperitif_nibble' ? null : SNACK_SLOTS.includes(type) ? (
                  <div className="setup-row" key={type}>
                    <span className="setup-label">{t[type]}</span>
                    <div className="row" style={{ gap: 6 }}>
                      {choiceButton(day, type, 'off', t.off)}
                      {choiceButton(day, type, 'kids', t.on)}
                    </div>
                  </div>
                ) : (
                <div className="setup-row" key={type}>
                  <span className="setup-label">{t[type]}</span>
                  <div className="row" style={{ gap: 6 }}>
                    {choiceButton(day, type, 'off', t.off)}
                    {choiceButton(day, type, 'everyone', t.everyone)}
                    {choiceButton(day, type, 'adults', t.adults)}
                    {choiceButton(day, type, 'kids', t.kids)}
                    {choiceButton(day, type, 'separate', t.separate)}
                    {day.choices[type] !== 'off' && type !== 'dessert' && (
                      <button
                        type="button"
                        title={t.quickHint}
                        className={`btn small${day.quick[type] ? ' on' : ''}`}
                        aria-pressed={Boolean(day.quick[type])}
                        onClick={() => toggleQuick(day.date, type)}
                      >
                        {t.quick}
                      </button>
                    )}
                    {day.choices[type] !== 'off' &&
                      GUEST_SLOTS.includes(type) &&
                      guestStepper(day, type)}
                  </div>
                  {day.choices[type] !== 'off' && peoplePicker(day, type)}
                </div>
                ),
              )}
            </div>
          ))}

          <div className="row" style={{ marginTop: 16 }}>
            <button className="btn primary" disabled={saving} onClick={() => void save()}>
              {saving ? t.saving : t.save}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
